import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext.tsx';
import { useRealtime } from './RealtimeContext.tsx';

// The unread chat number for the whole app (sidebar badge, browser tab title, chat launcher).
// The server is the source of truth, so the number is re-read from it whenever something that
// could change it happens (a new message, a chat being read, muted, archived...).

interface ChatUnreadContextType {
  totalUnread: number;
  unreadByChannel: Record<string, number>;
  refresh: () => void;
}

const ChatUnreadContext = createContext<ChatUnreadContextType>({
  totalUnread: 0,
  unreadByChannel: {},
  refresh: () => {},
});

export const ChatUnreadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, token } = useAuth();
  const { subscribe } = useRealtime();
  const [totalUnread, setTotalUnread] = useState(0);
  const [unreadByChannel, setUnreadByChannel] = useState<Record<string, number>>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isGuest = user?.role === 'guest';

  const load = useCallback(async () => {
    if (!token || !user || isGuest) {
      setTotalUnread(0);
      setUnreadByChannel({});
      return;
    }
    try {
      const res = await fetch('/api/chat/unread-summary', {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        setTotalUnread(Number(data.total) || 0);
        setUnreadByChannel(data.byChannel || {});
      }
    } catch {
      // keep the last known number; the next event or the periodic refresh will correct it
    }
  }, [token, user?.id, isGuest]);

  // Several events can arrive together (a message plus its read receipt), so refresh once
  const refresh = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      load();
    }, 350);
  }, [load]);

  useEffect(() => {
    load();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [load]);

  useEffect(() => {
    const unsubscribe = subscribe((event) => {
      switch (event.type) {
        case 'chat_message': {
          // Show the new message in the number at once; the server value follows right after
          const msg: any = event.data;
          if (msg && msg.senderId !== user?.id) setTotalUnread((n) => n + 1);
          refresh();
          break;
        }
        case 'channel_read':
        case 'channel_unread':
        case 'channel_muted':
        case 'channel_archived':
        case 'channel_members_updated':
        case 'message_deleted':
          refresh();
          break;
      }
    });
    return unsubscribe;
  }, [subscribe, refresh, user?.id]);

  // Components that mark a chat as read tell us through this window event
  useEffect(() => {
    window.addEventListener('chat-unread-refresh', refresh);
    window.addEventListener('focus', refresh);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    const interval = setInterval(refresh, 60000);
    return () => {
      window.removeEventListener('chat-unread-refresh', refresh);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(interval);
    };
  }, [refresh]);

  return (
    <ChatUnreadContext.Provider value={{ totalUnread, unreadByChannel, refresh }}>
      {children}
    </ChatUnreadContext.Provider>
  );
};

export const useChatUnread = () => useContext(ChatUnreadContext);
